import { z } from 'zod';

/**
 * Task history over the relay (harness to nine R3 fork, R4b rewind): request/reply pairs
 * correlated by `requestId` (the server's awaitSandboxEvent), never forwarded to the web.
 */

/**
 * Server → box (TASK_FORK_V1): copy the parent task's journal through the turn that ended at
 * `anchorUuid` (the assistant row's sdk_message_uuid) into the new child task's journal.
 * `taskId` is the PARENT, so the reply reaches the relay the request went out on.
 */
export const TaskForkRequestPayloadSchema = z.object({
  requestId: z.string(),
  taskId: z.string().uuid(),
  childTaskId: z.string().uuid(),
  anchorUuid: z.string().min(1),
});

/**
 * Box → server: the fork's outcome. `ok` with how many entries the child's journal holds (the
 * header included), the parent seq it was cut at, and the latest token usage the fork header
 * recorded (the parent's last round at or before the cut; absent when the copy holds none); else
 * a `reason`: `disabled` (the box's TASK_FORK_V1 is off), `not_found` (no journal the box can
 * read holds the anchor), `paused` (that turn ended on an ask still waiting for its answer, so the
 * child would start paused; nothing was written), `exists` (the child already has a journal;
 * nothing was written), `failed` (anything else; the child's file was removed).
 */
export const TaskForkResultPayloadSchema = z.object({
  requestId: z.string(),
  taskId: z.string(),
  ok: z.boolean(),
  entries: z.number().int().optional(),
  parentSeq: z.number().int().optional(),
  latestUsage: z.object({ inputTokens: z.number(), outputTokens: z.number(), model: z.string().optional() }).optional(),
  reason: z.enum(['disabled', 'not_found', 'paused', 'exists', 'failed']).optional(),
});

export type TaskForkRequestPayload = z.infer<typeof TaskForkRequestPayloadSchema>;
export type TaskForkResultPayload = z.infer<typeof TaskForkResultPayloadSchema>;

/**
 * Server → box (TASK_REWIND_V1): roll the task's journal back to the end of the turn that ended at
 * `anchorUuid` (the assistant row's sdk_message_uuid). Runs as a mailbox command on the task.
 */
export const TaskRewindRequestPayloadSchema = z.object({
  requestId: z.string(),
  taskId: z.string().uuid(),
  anchorUuid: z.string().min(1),
});

/**
 * Box → server: the rewind's outcome. `ok` with the journal seq it pivoted at and the asks it
 * closed (open asks whose call the rewind abandoned; the server cancels them); else a `reason`:
 * `disabled` (the box's TASK_REWIND_V1 is off), `turn_running` (a turn is open; nothing was
 * written), `stale` (the box could not catch its copy up with the mirror; nothing was written),
 * `awaiting_answer` (the task is paused on an ask; nothing was written), `not_found`
 * (the journal does not hold the anchor), `failed` (anything else).
 */
export const TaskRewindResultPayloadSchema = z.object({
  requestId: z.string(),
  taskId: z.string(),
  ok: z.boolean(),
  pivotSeq: z.number().int().optional(),
  closedInteractionIds: z.array(z.string()).optional(),
  reason: z.enum(['disabled', 'turn_running', 'stale', 'awaiting_answer', 'not_found', 'failed']).optional(),
});

export type TaskRewindRequestPayload = z.infer<typeof TaskRewindRequestPayloadSchema>;
export type TaskRewindResultPayload = z.infer<typeof TaskRewindResultPayloadSchema>;
